import { useState, useEffect, useCallback, useRef } from 'react';
import {
    List,
    useListContext,
    useGetList,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    TextField as MuiTextField,
    InputAdornment,
    Typography,
    alpha,
    IconButton,
    Tooltip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import DevicesIcon from '@mui/icons-material/DevicesOutlined';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import PersonBadgeIcon from '@mui/icons-material/BadgeOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmptyOutlined';
import TimerIcon from '@mui/icons-material/TimerOutlined';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../theme';

interface SessionStatus {
    sessionId: string;
    status: string;
    techSupportUserName: string | null;
    techSupportUserEmail: string | null;
    isOwner: boolean;
    requestedAt: string;
    startedAt: string | null;
    endedAt: string | null;
    timeRemaining: string | null;
    approvalExpiresInMinutes: number | null;
    reason: string | null;
    ticketNumber: string | null;
    approvedBy: string | null;
    denialReason: string | null;
    terminatedBy: string | null;
    terminationReason: string | null;
    canCancel: boolean;
    canTerminate: boolean;
    canDismiss: boolean;
}

interface DeviceRecord {
    id: string;
    nodeId: string;
    isActive: boolean;
    isRevoked: boolean;
    isExpired: boolean;
    hasAccessPoint: boolean;
    ownerId: string | null;
    ownerEmail: string | null;
    provisionedAt: string;
    lastSeenAt: string | null;
    lastSeenDisplay: string;
    expiresAt: string | null;
    revokedReason: string | null;
    status: string;
}

interface KpiCardProps {
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
}

const KpiCard = ({ title, value, icon, color }: KpiCardProps) => (
    <Card
        sx={{
            height: '100%',
            background: 'white',
            borderRadius: '12px',
            border: `1px solid ${colors.slate[100]}`,
            transition: 'all 0.2s',
            '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transform: 'translateY(-1px)',
            },
        }}
    >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" alignItems="center" gap={2}>
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: alpha(color, 0.1),
                        color: color,
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: colors.slate[900], lineHeight: 1 }}
                    >
                        {value}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: colors.slate[500], fontSize: 13, mt: 0.5 }}
                    >
                        {title}
                    </Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const StatusChip = ({ record }: { record: DeviceRecord }) => {
    if (record.isRevoked) {
        return (
            <Tooltip title={record.revokedReason || 'Revoked'}>
                <Chip
                    label="Revoked"
                    size="small"
                    icon={<CancelIcon sx={{ fontSize: 14 }} />}
                    sx={{ bgcolor: alpha(colors.error, 0.1), color: colors.error }}
                />
            </Tooltip>
        );
    }
    if (record.isExpired) {
        return (
            <Chip
                label="Expired"
                size="small"
                icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                sx={{ bgcolor: colors.slate[100], color: colors.slate[500] }}
            />
        );
    }
    if (record.isActive) {
        return (
            <Chip
                label="Active"
                size="small"
                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                sx={{ bgcolor: alpha(colors.success, 0.1), color: colors.success }}
            />
        );
    }
    return (
        <Chip
            label="Inactive"
            size="small"
            sx={{ bgcolor: colors.slate[100], color: colors.slate[500] }}
        />
    );
};

const OwnerCell = ({ record }: { record: DeviceRecord }) => {
    if (!record.ownerEmail) {
        return (
            <Typography sx={{ color: colors.slate[400], fontStyle: 'italic', fontSize: 13 }}>
                Not registered
            </Typography>
        );
    }

    const truncateEmail = (email: string) => {
        if (email.length <= 25) return email;
        const atIndex = email.indexOf('@');
        if (atIndex > 15) {
            return email.substring(0, 12) + '...' + email.substring(atIndex);
        }
        return email.substring(0, 22) + '...';
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PersonIcon sx={{ fontSize: 16, color: colors.primary }} />
            <Typography
                sx={{
                    color: colors.primary,
                    fontSize: 13,
                    fontWeight: 500,
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
                title={record.ownerEmail}
            >
                {truncateEmail(record.ownerEmail)}
            </Typography>
        </Box>
    );
};

const LastSeenCell = ({ record }: { record: DeviceRecord }) => {
    if (!record.lastSeenAt) {
        return (
            <Typography sx={{ color: colors.slate[400], fontStyle: 'italic', fontSize: 13 }}>
                Never
            </Typography>
        );
    }

    const lastSeen = new Date(record.lastSeenAt);
    const now = new Date();
    const hoursAgo = (now.getTime() - lastSeen.getTime()) / (1000 * 60 * 60);
    const isStale = hoursAgo > 24;

    return (
        <Typography
            sx={{
                color: isStale ? colors.warning : colors.slate[600],
                fontSize: 13,
            }}
            title={lastSeen.toLocaleString()}
        >
            {record.lastSeenDisplay}
        </Typography>
    );
};

const ExpiresCell = ({ record }: { record: DeviceRecord }) => {
    if (!record.expiresAt) {
        return (
            <Typography sx={{ color: colors.slate[400], fontStyle: 'italic', fontSize: 13 }}>
                No expiration
            </Typography>
        );
    }

    const expiresAt = new Date(record.expiresAt);
    const now = new Date();
    const daysUntil = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    const isExpired = daysUntil <= 0;
    const isExpiringSoon = daysUntil > 0 && daysUntil <= 7;
    const isExpiringWarning = daysUntil > 7 && daysUntil <= 30;

    const color = isExpired ? colors.error
        : isExpiringSoon ? colors.error
        : isExpiringWarning ? colors.warning
        : colors.slate[600];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography
                sx={{ color, fontSize: 13 }}
                title={expiresAt.toLocaleString()}
            >
                {expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
            {daysUntil > 0 && daysUntil <= 30 && !record.isRevoked && !record.isExpired && (
                <Typography
                    sx={{
                        color,
                        fontSize: 12,
                        fontWeight: 500,
                    }}
                >
                    {Math.floor(daysUntil)} days left
                </Typography>
            )}
        </Box>
    );
};

// Session status row that appears beneath a device with an active/pending session
interface SessionRowProps {
    nodeId: string;
    session: SessionStatus;
}

// Parse "HH:MM:SS" or "HH:MM:SS.ffffff" format to total seconds
const parseTimeToSeconds = (timeRemaining: string | null): number | null => {
    if (!timeRemaining) return null;
    const parts = timeRemaining.split(':');
    if (parts.length === 3) {
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        const seconds = parseFloat(parts[2]); // Handle fractional seconds
        return hours * 3600 + minutes * 60 + Math.floor(seconds);
    }
    return null;
};

// Format seconds to human-readable string
const formatSeconds = (totalSeconds: number): string => {
    if (totalSeconds <= 0) return 'Expired';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
    }
    if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
};

// Format date for display
const formatStartedDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
    });
};

const SessionRow = ({ nodeId, session }: SessionRowProps) => {
    const navigate = useNavigate();
    const isPending = session.status === 'PendingApproval';
    const isActive = session.status === 'Active';
    const statusColor = isPending ? '#f59e0b' : '#059669';

    // Live countdown timer
    const [remainingSeconds, setRemainingSeconds] = useState<number | null>(() =>
        parseTimeToSeconds(session.timeRemaining)
    );

    useEffect(() => {
        if (!isActive || remainingSeconds === null || remainingSeconds <= 0) return;

        const interval = setInterval(() => {
            setRemainingSeconds(prev => {
                if (prev === null || prev <= 0) return prev;
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isActive, remainingSeconds]);

    return (
        <TableRow sx={{
            bgcolor: 'transparent',
            '& td': {
                border: 'none',
                pt: 0,
                pb: 1,
            }
        }}>
            <TableCell colSpan={7} sx={{ px: 2 }}>
                <Box sx={{ display: 'flex', pl: 4 }}>
                    {/* Tree connector */}
                    <Box
                        sx={{
                            width: 20,
                            height: 28,
                            mr: 1,
                            mt: -1,
                            borderLeft: `2px solid ${statusColor}`,
                            borderBottom: `2px solid ${statusColor}`,
                            borderBottomLeftRadius: 6,
                            flexShrink: 0,
                        }}
                    />

                    {/* Session content */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            borderRadius: '8px',
                            borderLeft: `3px solid ${statusColor}`,
                            bgcolor: alpha(statusColor, 0.06),
                            flex: 1,
                        }}
                    >
                        {/* Status badge */}
                        <Chip
                            icon={isPending ?
                                <HourglassEmptyIcon sx={{ fontSize: 14 }} /> :
                                <PersonBadgeIcon sx={{ fontSize: 14 }} />
                            }
                            label={isPending ? 'Pending Approval' : 'Session Active'}
                            size="small"
                            sx={{
                                bgcolor: alpha(statusColor, 0.15),
                                color: statusColor,
                                fontWeight: 600,
                                fontSize: 12,
                                '& .MuiChip-icon': { color: statusColor },
                            }}
                        />

                        {/* Session details */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
                            {/* Tech support user */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: colors.slate[600], fontSize: 13 }}>
                                <PersonIcon sx={{ fontSize: 14, color: colors.slate[400] }} />
                                <span>{session.techSupportUserName || session.techSupportUserEmail}</span>
                                {session.isOwner && (
                                    <Chip
                                        label="You"
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: 10,
                                            fontWeight: 600,
                                            bgcolor: colors.primary,
                                            color: 'white',
                                            ml: 0.5,
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Started time (for active sessions) */}
                            {isActive && session.startedAt && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: colors.slate[500], fontSize: 12 }}>
                                    <ScheduleIcon sx={{ fontSize: 14, color: colors.slate[400] }} />
                                    <span>Started {formatStartedDate(session.startedAt)}</span>
                                </Box>
                            )}

                            {/* Time remaining (for active sessions) - live countdown */}
                            {isActive && remainingSeconds !== null && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: remainingSeconds <= 60 ? colors.error : colors.warning,
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 12,
                                    fontWeight: 600,
                                }}>
                                    <TimerIcon sx={{ fontSize: 14 }} />
                                    <span>{formatSeconds(remainingSeconds)}</span>
                                </Box>
                            )}

                            {/* Approval expires (for pending sessions) */}
                            {isPending && session.approvalExpiresInMinutes != null && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: statusColor,
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 12,
                                }}>
                                    <TimerIcon sx={{ fontSize: 14 }} />
                                    <span>{session.approvalExpiresInMinutes}m until expiry</span>
                                </Box>
                            )}

                            {/* Reason */}
                            {session.reason && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: colors.slate[500],
                                    fontSize: 12,
                                    fontStyle: 'italic',
                                }}>
                                    <span>"{session.reason}"</span>
                                </Box>
                            )}

                            {/* Ticket number */}
                            {session.ticketNumber && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: colors.slate[600],
                                    fontSize: 13,
                                }}>
                                    <ConfirmationNumberIcon sx={{ fontSize: 14, color: colors.slate[400] }} />
                                    <span>{session.ticketNumber}</span>
                                </Box>
                            )}
                        </Box>

                        {/* View details link */}
                        <Link
                            component="button"
                            onClick={() => navigate(`/devices/${nodeId}/show`)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontSize: 12,
                                fontWeight: 500,
                                color: colors.primary,
                                textDecoration: 'none',
                                p: 0.75,
                                borderRadius: '6px',
                                '&:hover': {
                                    bgcolor: alpha(colors.primary, 0.1),
                                },
                            }}
                        >
                            View Details
                            <ArrowForwardIcon sx={{ fontSize: 14 }} />
                        </Link>
                    </Box>
                </Box>
            </TableCell>
        </TableRow>
    );
};

const DeviceListHeader = () => {
    const { filterValues, setFilters, displayedFilters } = useListContext();
    const [search, setSearch] = useState(filterValues.q || '');
    const [filter, setFilter] = useState(filterValues.filter || 'active');

    // Get all devices for KPI calculation
    const { data: allDevices } = useGetList<DeviceRecord>('devices', {
        pagination: { page: 1, perPage: 1000 },
        filter: { filter: 'all' },
    });

    const devices = allDevices || [];
    const activeCount = devices.filter(d => d.isActive && !d.isRevoked && !d.isExpired).length;
    const totalCount = devices.length;
    const expiredCount = devices.filter(d => d.isExpired && !d.isRevoked).length;
    const revokedCount = devices.filter(d => d.isRevoked).length;

    // Debounced search.
    //
    // setFilters and the current filter state get a new identity on every list render, so
    // naming them as dependencies would restart the debounce on each render - including the
    // render this effect itself causes. They are read through a ref instead, which also fixes
    // a stale closure: the timer now merges into whatever the filters are when it fires, so a
    // filter toggled during the 300ms window is no longer overwritten by the value captured
    // when the user last typed.
    const latestFilterState = useRef({ filterValues, setFilters, displayedFilters });
    latestFilterState.current = { filterValues, setFilters, displayedFilters };

    useEffect(() => {
        const timer = setTimeout(() => {
            const current = latestFilterState.current;
            current.setFilters({ ...current.filterValues, q: search }, current.displayedFilters);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: string | null) => {
        if (newFilter) {
            setFilter(newFilter);
            setFilters({ ...filterValues, filter: newFilter }, displayedFilters);
        }
    };

    return (
        <Box mb={3}>
            {/* Page Header */}
            <Box mb={3}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: colors.slate[900] }}>
                    Provisioned Devices
                </Typography>
                <Typography variant="body2" sx={{ color: colors.slate[500], mt: 0.5 }}>
                    View and manage XTConnect nodes provisioned via bootstrap tokens
                </Typography>
            </Box>

            {/* KPI Summary Cards */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: 2,
                    mb: 3,
                }}
            >
                <KpiCard
                    title="Active"
                    value={activeCount}
                    icon={<CheckCircleIcon />}
                    color={colors.success}
                />
                <KpiCard
                    title="Total Provisioned"
                    value={totalCount}
                    icon={<DevicesIcon />}
                    color={colors.primary}
                />
                <KpiCard
                    title="Expired"
                    value={expiredCount}
                    icon={<ScheduleIcon />}
                    color={colors.warning}
                />
                <KpiCard
                    title="Revoked"
                    value={revokedCount}
                    icon={<CancelIcon />}
                    color={colors.error}
                />
            </Box>

            {/* Filter & Search Controls */}
            <Card sx={{ borderRadius: '12px', border: `1px solid ${colors.slate[100]}` }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: colors.slate[600] }}
                        >
                            Show:
                        </Typography>
                        <ToggleButtonGroup
                            value={filter}
                            exclusive
                            onChange={handleFilterChange}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 2,
                                    py: 1,
                                    borderRadius: '8px !important',
                                    border: 'none',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: 13,
                                    color: colors.slate[600],
                                    '&.Mui-selected': {
                                        bgcolor: 'white',
                                        color: colors.primary,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                                    },
                                },
                                bgcolor: colors.slate[100],
                                borderRadius: '10px',
                                p: 0.5,
                            }}
                        >
                            <ToggleButton value="active">
                                <CheckCircleIcon sx={{ fontSize: 16, mr: 0.75 }} />
                                Active Only
                            </ToggleButton>
                            <ToggleButton value="all">
                                <DevicesIcon sx={{ fontSize: 16, mr: 0.75 }} />
                                All
                            </ToggleButton>
                            <ToggleButton value="support">
                                <PersonBadgeIcon sx={{ fontSize: 16, mr: 0.75 }} />
                                In Support
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <MuiTextField
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by Node ID or email..."
                            size="small"
                            sx={{
                                ml: 2,
                                width: 280,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: 14,
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: colors.slate[400], fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ ml: 'auto' }}>
                            <Chip
                                label={`${totalCount} device(s)`}
                                size="small"
                                sx={{
                                    bgcolor: colors.slate[100],
                                    color: colors.slate[500],
                                    fontWeight: 500,
                                }}
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

// Helper to check dismissed sessions (shared with DeviceShow)
const dismissedSessionsKey = 'xtconnect-dismissed-sessions';
const isSessionDismissed = (sessionId: string): boolean => {
    try {
        const dismissed: string[] = JSON.parse(localStorage.getItem(dismissedSessionsKey) || '[]');
        return dismissed.includes(sessionId);
    } catch {
        return false;
    }
};

// Custom data grid that supports session rows
const DeviceDataGrid = () => {
    const { data, isLoading } = useListContext<DeviceRecord>();
    const navigate = useNavigate();
    const [sessions, setSessions] = useState<Record<string, SessionStatus>>({});

    // Fetch session statuses when devices change
    const fetchSessions = useCallback(async (devices: DeviceRecord[]) => {
        const devicesWithOwners = devices.filter(d => d.ownerId && d.isActive && !d.isRevoked);
        if (devicesWithOwners.length === 0) {
            setSessions({});
            return;
        }

        try {
            const response = await fetch('/api/admin/devices/sessions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    devices: devicesWithOwners.map(d => ({
                        nodeId: d.nodeId,
                        ownerId: d.ownerId,
                    })),
                }),
            });

            if (response.ok) {
                const sessionData = await response.json();
                setSessions(sessionData);
            }
        } catch (error) {
            console.error('Failed to fetch sessions:', error);
        }
    }, []);

    useEffect(() => {
        if (data && data.length > 0) {
            fetchSessions(data);
        }
    }, [data, fetchSessions]);

    if (isLoading) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', color: colors.slate[500] }}>
                Loading devices...
            </Box>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)}, ${alpha(colors.secondary, 0.1)})`,
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px',
                    }}
                >
                    <DevicesIcon sx={{ fontSize: 36, color: colors.primary }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: colors.slate[900], mb: 1 }}>
                    No Provisioned Devices Found
                </Typography>
                <Typography variant="body2" sx={{ color: colors.slate[500] }}>
                    Devices will appear here once they're provisioned using a bootstrap token.
                </Typography>
            </Box>
        );
    }

    const handleRowClick = (nodeId: string) => {
        navigate(`/devices/${nodeId}/show`);
    };

    return (
        <Table>
            <TableHead>
                <TableRow
                    sx={{
                        '& th': {
                            bgcolor: colors.slate[50],
                            fontWeight: 600,
                            color: colors.slate[600],
                            borderBottom: `1px solid ${colors.slate[200]}`,
                            fontSize: 12,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            py: 1.5,
                        },
                    }}
                >
                    <TableCell>Node ID</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Provisioned</TableCell>
                    <TableCell>Last Seen</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="right">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {data.map((device) => {
                    const session = sessions[device.nodeId];
                    const isInactive = device.isRevoked || device.isExpired || !device.isActive;

                    return [
                        // Device row
                        <TableRow
                            key={device.nodeId}
                            hover
                            onClick={() => handleRowClick(device.nodeId)}
                            sx={{
                                cursor: 'pointer',
                                opacity: isInactive ? 0.6 : 1,
                                '&:hover': { bgcolor: colors.slate[50] },
                                '& td': { py: 1.5 },
                            }}
                        >
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: '"JetBrains Mono", monospace',
                                            fontWeight: 600,
                                            fontSize: 13,
                                            padding: '4px 8px',
                                            background: colors.slate[100],
                                            borderRadius: '6px',
                                            color: isInactive ? colors.slate[500] : colors.slate[900],
                                        }}
                                    >
                                        {device.nodeId}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <OwnerCell record={device} />
                            </TableCell>
                            <TableCell>
                                <StatusChip record={device} />
                            </TableCell>
                            <TableCell>
                                {device.provisionedAt && (
                                    <Typography
                                        sx={{ fontSize: 13, color: colors.slate[600] }}
                                        title={new Date(device.provisionedAt).toLocaleString()}
                                    >
                                        {new Date(device.provisionedAt).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                        })}
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                <LastSeenCell record={device} />
                            </TableCell>
                            <TableCell>
                                <ExpiresCell record={device} />
                            </TableCell>
                            <TableCell align="right">
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Tooltip title="View Details">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/devices/${device.nodeId}/show`);
                                            }}
                                            sx={{
                                                border: `1px solid ${colors.slate[200]}`,
                                                borderRadius: '8px',
                                                '&:hover': {
                                                    bgcolor: colors.slate[50],
                                                    borderColor: colors.slate[300],
                                                },
                                            }}
                                        >
                                            <VisibilityIcon sx={{ fontSize: 18, color: colors.slate[600] }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </TableCell>
                        </TableRow>,
                        // Session row (if session exists and not dismissed)
                        session && !isSessionDismissed(session.sessionId) && (
                            <SessionRow
                                key={`${device.nodeId}-session`}
                                nodeId={device.nodeId}
                                session={session}
                            />
                        ),
                    ];
                })}
            </TableBody>
        </Table>
    );
};

export const DeviceList = () => (
    <List
        actions={false}
        component="div"
        sx={{
            '& .RaList-main': { mt: 0 },
        }}
    >
        <DeviceListHeader />
        <Card sx={{ borderRadius: '12px', border: `1px solid ${colors.slate[100]}`, overflow: 'hidden' }}>
            <DeviceDataGrid />
        </Card>
    </List>
);
